"use client";

import { useMemo, type ComponentType } from "react";
import { useLocale, useTranslations } from "next-intl";
import { clsx } from "clsx";
import type { Locale } from "@mw/shared/i18n";
import type { BillingPeriod } from "@mw/shared/pricing";
import { getPriceForCurrency } from "@mw/shared/pricing";
import { EventName } from "@mw/shared/analyticsEventNames";
import { useCurrency } from "@/app/_providers/currency";
import { formatPrice, formatPricePerMinute } from "@utilities/numbers";

import IconCircleCheck from "@ui/central-icons/icons/IconCircleCheck";
import IconCirclePlus from "@ui/central-icons/icons/IconCirclePlus";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { LinkTracked } from "@ui/components/core/LinkTracked";
import { publicUrls } from "@/constants";

import { TIER_COVER_IMAGES } from "../utils";
import type { CalculatorMode } from "../PricingTableContext";
import { CalculatorResultCardShell } from "./CalculatorResultCardShell";
import { getAgentsPlatformUsdPerMinute, recommendAgentsTier } from "./calculatorUtils";

const BULLET_LIST_CLASS = "-tw-mx-2 tw-list-none tw-pl-0 tw-space-y-2 lg:tw-space-y-3";
const CARD_HEADING_CLASS = "tw-text-white tw-type-5xl";
const CARD_CAPTION_CLASS = "tw-text-white/60 tw-type-xs";

interface CardBulletProps {
  children: React.ReactNode;
  icon?: ComponentType<{ className?: string }>;
  tone?: "default" | "muted";
}

function CardBullet({ children, icon: Icon = IconCircleCheck, tone = "default" }: CardBulletProps) {
  return (
    <li
      className={clsx(
        "tw-flex tw-items-center tw-gap-2 tw-type-sm tw-tabular-nums",
        tone === "muted" ? "tw-text-white/70" : "tw-text-white",
      )}
    >
      <Icon className={clsx("tw-size-5 tw-text-white/60 tw-flex-none", iconStrokeFix)} />
      <span>{children}</span>
    </li>
  );
}

// Slot reserved above the header so the layout doesn't shift when the badge appears or
// disappears as the recommendation crosses tier thresholds.
function MostPopularSlot({ label }: { label: string }) {
  return (
    <div className='-tw-mt-4 lg:-tw-mt-4 tw-mb-6 lg:tw-mb-8 tw-inline-flex tw-items-center tw-justify-center tw-border tw-border-white tw-text-white tw-px-3 tw-py-1 tw-rounded-lg tw-type-xs tw-font-medium tw-h-7 tw-w-fit'>
      {label}
    </div>
  );
}

interface CardHeaderProps {
  title: React.ReactNode;
  subtitle: string;
  price?: string;
  priceCaption?: string;
  isLoading?: boolean;
}

function CardHeader({ title, subtitle, price, priceCaption, isLoading }: CardHeaderProps) {
  const headingClass = clsx(CARD_HEADING_CLASS, "tw-tabular-nums tw-transition-opacity", isLoading && "tw-opacity-60");
  if (!price) {
    return (
      <div className='tw-mb-6 lg:tw-mb-8'>
        <h3 className={clsx(headingClass, "tw-mb-2")}>{title}</h3>
        <p className={CARD_CAPTION_CLASS}>{subtitle}</p>
      </div>
    );
  }
  return (
    <div className='tw-grid tw-grid-cols-[1fr_auto] tw-items-baseline tw-gap-x-3 tw-gap-y-2 tw-mb-6 lg:tw-mb-8'>
      <h3 className={headingClass}>{title}</h3>
      <p className={clsx(headingClass, "tw-text-right")}>{price}</p>
      <p className={CARD_CAPTION_CLASS}>{subtitle}</p>
      {priceCaption && <p className={clsx(CARD_CAPTION_CLASS, "tw-text-right")}>{priceCaption}</p>}
    </div>
  );
}

interface CardCtaFooterProps {
  href: string;
  label: string;
  eventProperties: Record<string, string>;
}

function CardCtaFooter({ href, label, eventProperties }: CardCtaFooterProps) {
  return (
    <div className='tw-mt-auto lg:-tw-mx-5 lg:-tw-mb-5 lg:tw-pt-6'>
      <LinkTracked
        eventName={EventName.mw_pricing_card_cta_clicked}
        eventProperties={eventProperties}
        asButton
        buttonVariants={{ variant: "elevated", size: "lg", expand: true }}
        className='!tw-rounded-2xl'
        href={href}
        label={label}
      />
    </div>
  );
}

export interface AgentsCalculatorResultCardProps {
  /** Selected model's LLM cost per minute, in USD (undefined while prices are unavailable). */
  pricePerMinute: number | undefined;
  /** Human-readable label of the selected model (e.g. "GPT-4o mini"). */
  modelName: string;
  /** Estimated conversation minutes per month. */
  minutesPerMonth: number;
  mode: CalculatorMode;
  period: BillingPeriod;
  isLoading: boolean;
}

/**
 * Result card for the agents LLM-cost calculator. In "subscription" mode it recommends the
 * cheapest ElevenAgents plan covering the estimated minutes (plan price + CTA) and surfaces the
 * estimated LLM usage as an add-on. In "payg" mode it shows the estimated total cost — platform
 * voice ($/min) plus LLM — for the configured minutes, since pay-as-you-go has no plan covering
 * platform minutes. Visual parity with {@link ApiCalculatorResultCard}.
 */
export function AgentsCalculatorResultCard({
  pricePerMinute,
  modelName,
  minutesPerMonth,
  mode,
  period,
  isLoading,
}: AgentsCalculatorResultCardProps) {
  const t = useTranslations("PricingClientKeys.AgentsCalculator");
  const tCard = useTranslations("PricingClientKeys.PricingCard");
  const locale = useLocale() as Locale;
  const { currency, currencySymbol } = useCurrency();

  // Standard ElevenAgents platform voice rate ($/min), uniform across self-serve plans. Sourced
  // from shared pricing so it tracks config. Subscription plans bundle these minutes in the plan
  // price, so it only enters the PAYG total.
  const platformUsdPerMinute = getAgentsPlatformUsdPerMinute();

  // Estimated monthly LLM spend in the visitor's currency — usage-based on every plan, shown as an
  // add-on in subscription mode.
  const monthlyLlmCostFormatted = useMemo(() => {
    const usd = (pricePerMinute ?? 0) * minutesPerMonth;
    return formatPrice(getPriceForCurrency(usd, currency), currencySymbol, locale);
  }, [pricePerMinute, minutesPerMonth, currency, currencySymbol, locale]);

  // PAYG headline: the realistic total a visitor pays with no plan — platform voice + LLM, per minute.
  const paygTotalFormatted = useMemo(() => {
    const usd = (platformUsdPerMinute + (pricePerMinute ?? 0)) * minutesPerMonth;
    return formatPrice(getPriceForCurrency(usd, currency), currencySymbol, locale);
  }, [platformUsdPerMinute, pricePerMinute, minutesPerMonth, currency, currencySymbol, locale]);

  // Per-minute rates stay in USD — currency formatting rounds to cents, collapsing sub-cent LLM
  // rates. LLM uses 4-decimal precision (mirrors the LLM-price table); the platform rate is a round
  // figure shown at 2 decimals.
  const perMinuteUsd = pricePerMinute !== undefined ? `$${formatPricePerMinute(pricePerMinute)}` : undefined;
  const platformRateUsd = `$${platformUsdPerMinute.toFixed(2)}`;
  const formattedMinutes = minutesPerMonth.toLocaleString(locale);

  const recommendation = useMemo(
    () => (mode === "subscription" ? recommendAgentsTier(minutesPerMonth, period) : null),
    [mode, minutesPerMonth, period],
  );

  if (mode === "subscription" && recommendation) {
    const { tier, displayPrice, includedMinutes, isEnterprise } = recommendation;
    const tierName = tCard(`tiers.${tier.planId}.title` as Parameters<typeof tCard>[0]);
    const coverImage = TIER_COVER_IMAGES[tier.planId] ?? TIER_COVER_IMAGES.payg;
    const formattedPrice =
      displayPrice !== undefined
        ? formatPrice(getPriceForCurrency(displayPrice, currency), currencySymbol, locale)
        : undefined;
    const isMostPopular = tier.planId === "creator";

    return (
      <CalculatorResultCardShell bg='black' coverImage={coverImage}>
        {isEnterprise || !formattedPrice ? (
          <CardHeader title={tierName} subtitle={t("subscription_enterprise_subtitle")} />
        ) : (
          <CardHeader
            title={tierName}
            subtitle={t("subscription_subtitle")}
            price={formattedPrice}
            priceCaption={period === "yearly" ? t("per_month_yearly") : t("per_month")}
          />
        )}
        {isMostPopular && <MostPopularSlot label={t("badge_most_popular")} />}

        <ul className={clsx(BULLET_LIST_CLASS, "tw-mb-6 lg:tw-mb-0")}>
          {!isEnterprise && (
            <CardBullet>{t("includes_minutes", { minutes: includedMinutes.toLocaleString(locale) })}</CardBullet>
          )}
          {/* Check (not +) + muted + "deducted from credits" note: on a plan the LLM spend draws
              down the plan's included credits rather than being billed on top, so it reads as
              something the plan covers, not an extra charge. */}
          <CardBullet icon={IconCircleCheck} tone='muted'>
            {isLoading ? (
              <span className='motion-safe:tw-animate-pulse'>{t("llm_addon_loading")}</span>
            ) : (
              t("llm_addon", { cost: monthlyLlmCostFormatted, model: modelName })
            )}
          </CardBullet>
          <CardBullet>{isEnterprise ? t("enterprise_volume") : t("cancel_anytime")}</CardBullet>
        </ul>

        <CardCtaFooter
          href={tier.cta.href}
          label={isEnterprise || !formattedPrice ? t("cta_contact_sales") : t("cta_start_with", { tier: tierName })}
          eventProperties={{ tier: tier.planId, source: "agents_calculator_subscription" }}
        />
      </CalculatorResultCardShell>
    );
  }

  return (
    <CalculatorResultCardShell bg='black' coverImage={TIER_COVER_IMAGES.payg}>
      <CardHeader title={paygTotalFormatted} subtitle={t("estimated_cost")} isLoading={isLoading} />

      <ul className={clsx(BULLET_LIST_CLASS, "tw-mb-2 lg:tw-mb-3")}>
        <CardBullet icon={IconCirclePlus} tone='muted'>
          {perMinuteUsd ? t("payg_llm_line", { model: modelName, rate: perMinuteUsd }) : modelName}
        </CardBullet>
        <CardBullet icon={IconCirclePlus} tone='muted'>
          {t("payg_platform_line", { rate: platformRateUsd })}
        </CardBullet>
        <CardBullet icon={IconCirclePlus} tone='muted'>
          {t("payg_minutes", { minutes: formattedMinutes })}
        </CardBullet>
      </ul>

      <ul className={clsx(BULLET_LIST_CLASS, "tw-mb-6 lg:tw-mb-0")}>
        <CardBullet>{t("payg_no_commitment")}</CardBullet>
        <CardBullet>{t("payg_passthrough")}</CardBullet>
      </ul>

      <CardCtaFooter
        href={publicUrls.signUp}
        label={t("cta_get_started")}
        eventProperties={{ tier: "payg", source: "agents_calculator_payg" }}
      />
    </CalculatorResultCardShell>
  );
}
